import React, { useState, useEffect, useContext } from 'react';
import { Layout, Typography, Spin, message, Button, Space, Tabs } from 'antd';
import { ArrowLeftOutlined, PlayCircleOutlined, HistoryOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import EqualOffenseStarter from '../components/EqualOffense/EqualOffenseStarter';
import EqualOffenseTracker from '../components/EqualOffense/EqualOffenseTracker';
import EqualOffenseMatchplay from '../components/EqualOffense/EqualOffenseMatchplay';
import EqualOffenseHistory from '../components/EqualOffense/EqualOffenseHistory';
import {
  getSessions, createSession, finishSession,
  getFramesBySession, createFrame, deleteFrame
} from '../api/services/equalOffenseService';
import { getPlayers } from '../api/services/playerService';

const { Content, Header } = Layout;
const { Title } = Typography;

/**
 * EqualOffensePage
 * Main container page for Equal Offense training
 */
const EqualOffensePage = () => {
  const navigate = useNavigate();
  const { currentUser } = useContext(AuthContext);

  // State
  const [activeSession, setActiveSession] = useState(null);
  const [frames, setFrames] = useState([]);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [frameLoading, setFrameLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('play');

  // For matchplay
  const [player1Frames, setPlayer1Frames] = useState([]);
  const [player2Frames, setPlayer2Frames] = useState([]);
  const [opponent, setOpponent] = useState(null);

  // Load players on mount
  useEffect(() => {
    loadPlayers();
    checkActiveSession();
  }, []);

  const loadPlayers = async () => {
    try {
      const data = await getPlayers();
      setPlayers(data);
    } catch (error) {
      console.error('Failed to load players:', error);
    }
  };

  const checkActiveSession = async () => {
    setLoading(true);
    try {
      const sessions = await getSessions();
      // Find unfinished session for current player
      const active = sessions.find(s => !s.is_finished && s.player === currentUser?.player_id);
      if (active) {
        setActiveSession(active);
        await loadFrames(active.id, active);
      }
    } catch (error) {
      console.error('Failed to check active session:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFrames = async (sessionId, session = activeSession) => {
    try {
      const data = await getFramesBySession(sessionId);
      setFrames(data);

      // For matchplay, split frames by player
      if (session?.is_matchplay) {
        const currentPlayerId = currentUser?.player_id;
        const opponentId = session.opponent;

        setPlayer1Frames(data.filter(f => f.player === currentPlayerId));
        setPlayer2Frames(data.filter(f => f.player === opponentId));

        // Set opponent info
        const opponentPlayer = players.find(p => p.id === opponentId);
        setOpponent(opponentPlayer);
      }
    } catch (error) {
      console.error('Failed to load frames:', error);
    }
  };

  const handleStartSession = async (sessionData) => {
    setLoading(true);
    try {
      const newSession = await createSession({
        ...sessionData,
        player: currentUser?.player_id,
      });
      setActiveSession(newSession);
      setFrames([]);
      setPlayer1Frames([]);
      setPlayer2Frames([]);

      if (sessionData.is_matchplay && sessionData.opponent) {
        const opponentPlayer = players.find(p => p.id === sessionData.opponent);
        setOpponent(opponentPlayer);
      }

      message.success('Session gestartet!');
    } catch (error) {
      console.error('Failed to start session:', error);
      message.error('Fehler beim Starten der Session.');
    } finally {
      setLoading(false);
    }
  };

  const handleAddFrame = async (frameData) => {
    setFrameLoading(true);
    try {
      const result = await createFrame(frameData);
      await loadFrames(activeSession.id);

      // Reload session to get updated stats
      const sessions = await getSessions();
      const updated = sessions.find(s => s.id === activeSession.id);
      if (updated) {
        setActiveSession(updated);

        // Prüfen ob Session automatisch beendet wurde
        if (result.session_finished || updated.is_finished) {
          message.success(`Session beendet! Gesamtpunkte: ${updated.total_points}`);
          // Session als abgeschlossen markieren und zurück zum Start
          setActiveSession(null);
          setFrames([]);
        } else if (result.remaining_innings !== null && result.remaining_innings <= 2) {
          message.info(`Noch ${result.remaining_innings} Aufnahme(n) übrig!`);
        }
      }
    } catch (error) {
      console.error('Failed to add frame:', error);
      message.error('Fehler beim Speichern der Aufnahme.');
    } finally {
      setFrameLoading(false);
    }
  };

  const handleDeleteFrame = async (frameId) => {
    try {
      await deleteFrame(frameId);
      await loadFrames(activeSession.id);

      // Reload session to get updated stats
      const sessions = await getSessions();
      const updated = sessions.find(s => s.id === activeSession.id);
      if (updated) {
        setActiveSession(updated);
      }

      message.success('Aufnahme gelöscht.');
    } catch (error) {
      console.error('Failed to delete frame:', error);
      message.error('Fehler beim Löschen der Aufnahme.');
    }
  };

  const handleFinishSession = async (winnerId = null) => {
    setLoading(true);
    try {
      await finishSession(activeSession.id, winnerId);
      message.success('Session beendet!');
      setActiveSession(null);
      setFrames([]);
      setPlayer1Frames([]);
      setPlayer2Frames([]);
      setOpponent(null);
    } catch (error) {
      console.error('Failed to finish session:', error);
      message.error('Fehler beim Beenden der Session.');
    } finally {
      setLoading(false);
    }
  };

  const currentPlayer = players.find(p => p.id === currentUser?.player_id);

  if (loading && !activeSession) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Content style={{ padding: 24, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Spin size="large" />
        </Content>
      </Layout>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Header style={{ background: '#fff', padding: '0 16px', display: 'flex', alignItems: 'center' }}>
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/dashboard')}
          >
            Zurück
          </Button>
          <Title level={4} style={{ margin: 0 }}>Equal Offense Training</Title>
        </Space>
      </Header>

      <Content style={{ padding: 16 }}>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'play',
              label: (
                <span>
                  <PlayCircleOutlined />
                  {activeSession ? 'Aktive Session' : 'Spielen'}
                </span>
              ),
              children: activeSession ? (
                // Active session - show tracker or matchplay
                activeSession.is_matchplay ? (
                  <EqualOffenseMatchplay
                    session={activeSession}
                    player1={currentPlayer}
                    player2={opponent}
                    player1Frames={player1Frames}
                    player2Frames={player2Frames}
                    onAddFrame={handleAddFrame}
                    onDeleteFrame={handleDeleteFrame}
                    onFinishSession={handleFinishSession}
                    loading={frameLoading}
                  />
                ) : (
                  <EqualOffenseTracker
                    session={activeSession}
                    frames={frames}
                    onAddFrame={handleAddFrame}
                    onDeleteFrame={handleDeleteFrame}
                    onFinishSession={handleFinishSession}
                    loading={frameLoading}
                  />
                )
              ) : (
                // No active session - show starter
                <EqualOffenseStarter
                  onStart={handleStartSession}
                  players={players}
                  currentPlayerId={currentUser?.player_id}
                  loading={loading}
                />
              ),
            },
            {
              key: 'history',
              label: (
                <span>
                  <HistoryOutlined />
                  Verlauf
                </span>
              ),
              children: (
                <EqualOffenseHistory playerId={currentUser?.player_id} />
              ),
            },
          ]}
        />
      </Content>
    </Layout>
  );
};

export default EqualOffensePage;
